import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });

  const fadeInVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <AboutSection id="about" ref={ref}>
      <div className="container">
        <SectionHeader
          variants={fadeInVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          transition={{ duration: 0.6 }}
        >
          <h2>About Me</h2>
          <Divider />
        </SectionHeader>

        <AboutContent>
          <AboutImageContainer
            variants={fadeInVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AboutImage src={process.env.PUBLIC_URL + '/assets/profile-photo.jpg'} alt="Amrit Prasad Phuyal" />
            <ImageDecoration />
          </AboutImageContainer>

          <AboutTextContent>
            <motion.h3
              variants={fadeInVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Who am I?
            </motion.h3>

            <motion.p
              variants={fadeInVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              I'm a Data Engineer with expertise in building scalable data pipelines using PySpark,
              Databricks, Fabric, Azure, and AWS. I have proven experience in real-time data processing,
              Data Quality (DQ) frameworks, and end-to-end ELT development.
            </motion.p>

            <motion.p
              variants={fadeInVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Certified in Databricks (Data Engineer Associate), Microsoft Fabric (Data Engineer & Analytics
              Engineer Associate), and Airflow Fundamentals, I bring hands-on expertise in Spark Streaming,
              Delta Lake, and cloud-based data solutions.
            </motion.p>

            <motion.p
              variants={fadeInVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              I have a strong background in mentoring teams, leading technical interviews, and driving adoption
              of modern data architectures. I graduated from the Institute of Engineering, Pulchowk Campus with
              a Bachelor's degree in Electronics & Communication Engineering.
            </motion.p>

            <TechnologiesSection
              variants={fadeInVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <h4>Technologies I work with:</h4>
              <TechnologiesList>
                <TechnologyItem>Python</TechnologyItem>
                <TechnologyItem>SQL</TechnologyItem>
                <TechnologyItem>Apache Spark</TechnologyItem>
                <TechnologyItem>PySpark</TechnologyItem>
                <TechnologyItem>Apache Airflow</TechnologyItem>
                <TechnologyItem>Databricks</TechnologyItem>
                <TechnologyItem>Delta Lake</TechnologyItem>
                <TechnologyItem>Microsoft Fabric</TechnologyItem>
                <TechnologyItem>AWS</TechnologyItem>
                <TechnologyItem>Azure</TechnologyItem>
              </TechnologiesList>
            </TechnologiesSection>
          </AboutTextContent>
        </AboutContent>
      </div>
    </AboutSection>
  );
};

// Styled Components
const AboutSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
    }
`;

const SectionHeader = styled(motion.div)`
    margin-bottom: 60px;
    text-align: center;

    h2 {
        font-size: 2.5rem;
        color: var(--text-light);
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
`;

const Divider = styled.div`
    height: 4px;
    width: 70px;
    background-color: var(--accent);
    margin: 0 auto;
`;

const AboutContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: 50px;
    align-items: center;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }
`;

const AboutImageContainer = styled(motion.div)`
    position: relative;
    margin: 0 auto;
    max-width: 400px;
    width: 100%;

    @media (max-width: 992px) {
        margin-bottom: 40px;
    }
`;

const AboutImage = styled.img`
    width: 100%;
    border-radius: 6px;
    position: relative;
    z-index: 2;
    filter: grayscale(30%);
    transition: all 0.3s ease;
    box-shadow: 0 10px 30px -15px var(--shadow);

    &:hover {
        filter: grayscale(0%);
        box-shadow: 0 20px 30px -15px var(--shadow);
    }
`;

const ImageDecoration = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px solid var(--accent);
    border-radius: 6px;
    top: 20px;
    left: 20px;
    z-index: 1;
    transition: all 0.3s ease;

    ${AboutImageContainer}:hover & {
        top: 15px;
        left: 15px;
    }
`;

const AboutTextContent = styled.div`
    h3 {
        color: var(--accent);
        font-size: 1.8rem;
        margin-bottom: 20px;
    }

    p {
        color: var(--text-secondary);
        font-size: 1.1rem;
        line-height: 1.7;
        margin-bottom: 20px;
    }
`;

const TechnologiesSection = styled(motion.div)`
    margin-top: 40px;

    h4 {
        font-size: 1.2rem;
        color: var(--text-light);
        margin-bottom: 15px;
    }
`;

const TechnologiesList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    list-style-type: none;
    padding: 0;
`;

const TechnologyItem = styled.li`
    color: var(--text-secondary);
    background-color: rgba(30, 136, 229, 0.1);
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 0.9rem;
    border: 1px solid var(--accent);
`;

export default About;
